import React, { useState } from 'react';
import { AnimatePresence } from 'motion/react';
import {
  ReferralPageWrapper, DentistSection, PatientSection, FormFooter, FormSection,
  SuccessScreen, RadioGroup, CheckGroup, Fw, icls,
  DENTIST_DEFAULTS, PATIENT_DEFAULTS, FOOTER_DEFAULTS,
  validateCommon, submitReferral,
} from './ReferralBase';

const SCAN_REASON_OPTIONS = [
  { value: 'implants', label: 'Implants' },
  { value: 'ortho', label: 'Orthodontics' },
  { value: 'impacted', label: 'Impacted Teeth' },
  { value: 'endo', label: 'Endodontics' },
  { value: 'sinus', label: 'Sinus Exam' },
];

const FIELD_OPTIONS = [
  { value: 'small5x5', label: 'Small Field 5×5' },
  { value: 'dualJaw', label: 'Dual Jaw' },
  { value: 'mandible', label: 'Mandible' },
  { value: 'maxilla', label: 'Maxilla' },
  { value: 'opg', label: 'True 2D OPG' },
];

const DEFAULTS = {
  ...DENTIST_DEFAULTS, ...PATIENT_DEFAULTS,
  scanReasons: [], fieldOfView: '', areaOfInterest: '',
  irmer: false, specialInstructions: '',
  ...FOOTER_DEFAULTS,
};

function validate(form) {
  const errors = validateCommon(form);
  if (!form.scanReasons.length) errors.scanReasons = 'Please select at least one reason';
  if (!form.fieldOfView) errors.fieldOfView = 'Please select field of view';
  return errors;
}

export default function CBCTReferral() {
  const [form, setForm] = useState(DEFAULTS);
  const [errors, setErrors] = useState({});
  const [focused, setFocused] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'scanReasons') {
      setForm((f) => ({
        ...f,
        scanReasons: checked ? [...f.scanReasons, value] : f.scanReasons.filter((v) => v !== value),
      }));
      if (errors.scanReasons) setErrors((err) => ({ ...err, scanReasons: '' }));
    } else {
      setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
      if (errors[name]) setErrors((err) => ({ ...err, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate(form);
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSubmitting(true);
    try { await submitReferral({ ...form, scanReasons: form.scanReasons.join(', ') }, 'CBCT'); } catch (_) {}
    setSubmitting(false);
    setSubmitted(true);
  };

  const reset = () => { setForm(DEFAULTS); setErrors({}); setSubmitted(false); };
  const inputCls = (f) => icls(f, errors, focused);
  const commonProps = { form, errors, focused, onChange: handleChange, onFocus: setFocused, onBlur: () => setFocused(null) };

  return (
    <ReferralPageWrapper
      title="CBCT Referral"
      subtitle="Refer your patient for cone beam computed tomography scanning with detailed specialist reporting."
      photo="/images/referral/referral-cbct.jpg">
      <h2 className="font-serif text-forest text-2xl font-semibold mb-1">CBCT Referral Form</h2>
      <p className="text-gray-500 text-sm mb-7">All fields marked <span className="text-red-400">*</span> are required.</p>

      <AnimatePresence mode="wait">
        {submitted ? (
          <SuccessScreen key="success" patientName={form.patientName} onReset={reset} />
        ) : (
          <form key="form" onSubmit={handleSubmit} noValidate>
            <DentistSection {...commonProps} />
            <PatientSection {...commonProps} />

            <FormSection title="Scan Requirements">
              <div className="grid gap-5">
                <CheckGroup name="scanReasons" label="Reason(s) for Scan" required
                  options={SCAN_REASON_OPTIONS} values={form.scanReasons}
                  onChange={handleChange} error={errors.scanReasons} />
                <RadioGroup name="fieldOfView" label="Field of View / Area of Interest" required
                  options={FIELD_OPTIONS} value={form.fieldOfView}
                  onChange={handleChange} error={errors.fieldOfView} />
                <Fw>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor="areaOfInterest">
                    Specific Teeth / Area (tooth numbers or description)
                  </label>
                  <input id="areaOfInterest" name="areaOfInterest" type="text" value={form.areaOfInterest}
                    onChange={handleChange} placeholder="e.g. UR8, LR5-LR7"
                    onFocus={() => setFocused('areaOfInterest')} onBlur={() => setFocused(null)}
                    className={inputCls('areaOfInterest')} />
                </Fw>
              </div>
            </FormSection>

            <FormSection title="Reporting & Compliance">
              <Fw>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" name="irmer" checked={form.irmer} onChange={handleChange}
                    className="mt-0.5 w-4 h-4 accent-forest flex-shrink-0" />
                  <span className="text-sm text-gray-600">
                    I confirm this referral has been justified in accordance with <strong>IRMER 2017</strong> regulations and the clinical benefit outweighs the radiation risk.
                  </span>
                </label>
              </Fw>
            </FormSection>

            <FormSection title="Additional Information">
              <Fw>
                <label className="block text-sm font-medium text-gray-700 mb-1.5" htmlFor="specialInstructions">
                  Special Instructions
                </label>
                <textarea id="specialInstructions" name="specialInstructions" rows={3} value={form.specialInstructions}
                  onChange={handleChange} placeholder="Any other relevant clinical information…"
                  onFocus={() => setFocused('specialInstructions')} onBlur={() => setFocused(null)}
                  className={`${inputCls('specialInstructions')} resize-none`} />
              </Fw>
            </FormSection>

            <FormFooter {...commonProps} submitting={submitting} />
          </form>
        )}
      </AnimatePresence>
    </ReferralPageWrapper>
  );
}
